<script setup>
// BonBoZ: chồng thẻ "khách mới nhắn" ở góc trên phải, dưới navbar 56px.
//
// Bộ máy (lọc, hết hạn, dedupe theo hội thoại) nằm hết ở
// `dashboard/helper/newMessagePing.js`. File này chỉ vẽ và điều hướng.
// Khuôn xếp chồng bám theo `components-next/call/FloatingCallWidget.vue`.
// Màu để trong `_bonboz-theme.scss` (`.bonboz-ping-*`) vì eslint cấm style tĩnh
// trong template — và vì mọi màu BonBoZ đều phải là `rgb(var(--pk-*))`.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { messageStamp } from 'shared/helpers/timeHelper';
import { loadPageConfigs, pageColorOf } from 'dashboard/helper/pageConfig';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import {
  pings,
  clockTick,
  PING_TTL_MS,
  MAX_VISIBLE_PINGS,
  pausePing,
  resumePing,
  dismissPing,
} from 'dashboard/helper/newMessagePing';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getInbox = useMapGetter('inboxes/getInbox');
const getDraft = useMapGetter('draftMessages/get');
const { matchesSelection, setSelectedInboxes } = useSelectedPages();

loadPageConfigs();

const visiblePings = computed(() => pings.value.slice(0, MAX_VISIBLE_PINGS));
const overflowCount = computed(() =>
  Math.max(0, pings.value.length - MAX_VISIBLE_PINGS)
);

const inboxNameOf = inboxId => getInbox.value(inboxId)?.name || '';

// Vạch đếm ngược: `pausedAt` khác 0 thì đóng băng đúng phần còn lại lúc rê chuột.
const remainingPercent = ping => {
  const now = ping.pausedAt || clockTick.value;
  const left = Math.max(0, Math.min(PING_TTL_MS, ping.expiresAt - now));
  return (left / PING_TTL_MS) * 100;
};

// Bản nháp Chatwoot lưu ở store `draftMessages` với khoá
// `draft-<conversationId>-<replyType>`, ghi lại mỗi lần gõ (watcher `message()`
// trong ReplyBox.vue). Chỉ hỏi khi hội thoại ĐANG MỞ còn chữ chưa gửi.
// ponytail: `saveDraft` lưu thẳng `this.message`, mà chữ ký được nối vào chính
// biến đó. Page bật chữ ký sẽ có nháp không bao giờ rỗng -> hỏi thừa. Chưa sửa
// vì chữ ký chưa tồn tại trong bản này. Khi có: bọc bằng `removeSignature`
// (`dashboard/helper/editorHelper`), xem `hasMeaningfulEditorContent()` trong
// ReplyBox.vue - Chatwoot đã giải đúng bài này, chỉ việc dùng lại.
const hasUnsentDraft = () => {
  const openConversationId = store.getters.getSelectedChat?.id;
  if (!openConversationId) return false;
  return ['reply', 'note'].some(replyType =>
    (getDraft.value(`draft-${openConversationId}-${replyType}`) || '').trim()
  );
};

const openConversation = ping => {
  const accountId = route.params.accountId;
  if (!accountId || !ping.conversationId) return;
  if (
    hasUnsentDraft() &&
    // eslint-disable-next-line no-alert
    !window.confirm(t('BONBOZ.NEW_MESSAGE_PING.CONFIRM_LEAVE_DRAFT'))
  ) {
    return;
  }
  dismissPing(ping.conversationId);
  // Đang lọc trang khác thì hội thoại mở được ở khung phải nhưng cột trái không
  // chứa nó — nhân viên mất chỗ đứng, không thấy tin trước tin sau của khách.
  // Lựa chọn hiện tại đã bao gồm trang này (kể cả "tất cả") thì để yên, đừng
  // phá bộ lọc nhiều trang mà người ta cố ý đặt.
  if (ping.inboxId && !matchesSelection({ inbox_id: ping.inboxId })) {
    setSelectedInboxes([ping.inboxId]);
  }
  router.push({
    path: frontendURL(conversationUrl({ accountId, id: ping.conversationId })),
  });
};
</script>

<template>
  <div
    v-if="pings.length"
    class="fixed top-[60px] ltr:right-4 rtl:left-4 z-50 flex flex-col gap-2 w-[calc(100vw-2rem)] sm:w-[360px]"
  >
    <button
      v-for="ping in visiblePings"
      :key="ping.conversationId"
      type="button"
      class="overflow-hidden relative w-full text-left rounded-lg shadow-lg cursor-pointer bonboz-ping-card"
      :aria-label="
        t('BONBOZ.NEW_MESSAGE_PING.OPEN', {
          name: ping.senderName || t('BONBOZ.NEW_MESSAGE_PING.UNKNOWN'),
        })
      "
      @mouseenter="pausePing(ping)"
      @mouseleave="resumePing(ping)"
      @focus="pausePing(ping)"
      @blur="resumePing(ping)"
      @click="openConversation(ping)"
    >
      <div class="flex gap-3 items-start p-3 pb-4">
        <Avatar
          :src="ping.senderAvatar"
          :name="ping.senderName || '?'"
          :size="36"
          rounded-full
          class="flex-shrink-0 rounded-full"
        />
        <div class="flex-1 min-w-0">
          <div class="flex gap-2 items-baseline">
            <span
              class="flex-1 text-sm font-semibold truncate bonboz-ping-name"
            >
              {{ ping.senderName || t('BONBOZ.NEW_MESSAGE_PING.UNKNOWN') }}
            </span>
            <span class="flex-shrink-0 text-xs bonboz-ping-time">
              {{ messageStamp(ping.createdAt) }}
            </span>
          </div>
          <p class="mt-0.5 text-sm line-clamp-2 bonboz-ping-text">
            {{ ping.content || t('BONBOZ.NEW_MESSAGE_PING.ATTACHMENT') }}
          </p>
          <div
            v-if="inboxNameOf(ping.inboxId)"
            class="flex gap-1.5 items-center mt-1"
          >
            <span
              class="flex-shrink-0 w-2 h-2 rounded-full bonboz-ping-dot"
              :style="{ backgroundColor: pageColorOf(ping.inboxId) }"
            />
            <span class="text-xs truncate bonboz-ping-page">
              {{ inboxNameOf(ping.inboxId) }}
            </span>
          </div>
        </div>
      </div>
      <span
        class="absolute bottom-0 left-0 h-0.5 bonboz-ping-bar"
        :style="{ width: `${remainingPercent(ping)}%` }"
      />
    </button>

    <!-- Dòng gộp phần dư. Không bấm được ở bản này — đúng đặc tả. -->
    <div
      v-if="overflowCount"
      class="px-3 py-1.5 text-xs rounded-lg shadow bonboz-ping-more"
    >
      {{ t('BONBOZ.NEW_MESSAGE_PING.MORE', { count: overflowCount }) }}
    </div>
  </div>
</template>
